import React, { useState, useEffect } from 'react';
import { Tags, Trash2, Edit2, Save, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { apiRequest } from '../../../utils/api-request';

interface CrmTag {
  id: number;
  uuid: string;
  tag_name: string;
  tag_description: string;
  is_active: boolean;
  created_at: string;
}

const CrmTagsPage: React.FC = () => {
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingTag, setEditingTag] = useState<CrmTag | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newTag, setNewTag] = useState({ tag_name: '', tag_description: '' });

  // Load tags from API
  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest('/shopify/crm-tags', { method: 'GET' });
      if (response.success && response.data) {
        setTags(response.data);
      }
    } catch (error) {
      console.error('Failed to load CRM tags:', error);
      setMessage({ type: 'error', text: 'Failed to load CRM tags.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.tag_name.trim()) {
      setMessage({ type: 'error', text: 'Tag name is required.' });
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiRequest('/shopify/crm-tags', {
        method: 'POST',
        body: newTag
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Tag created successfully!' });
        setNewTag({ tag_name: '', tag_description: '' });
        setIsAddingNew(false);
        loadTags();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to create tag.' });
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
      setMessage({ type: 'error', text: 'Failed to create tag.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;

    setIsSaving(true);
    try {
      const response = await apiRequest(`/shopify/crm-tags/${editingTag.uuid}`, {
        method: 'PUT',
        body: {
          tag_name: editingTag.tag_name,
          tag_description: editingTag.tag_description,
          is_active: editingTag.is_active
        }
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Tag updated successfully!' });
        setEditingTag(null);
        loadTags();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to update tag.' });
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
      setMessage({ type: 'error', text: 'Failed to update tag.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTag = async (tag: CrmTag) => {
    if (!confirm(`Are you sure you want to delete "${tag.tag_name}"?`)) return;

    try {
      const response = await apiRequest(`/shopify/crm-tags/${tag.uuid}`, {
        method: 'DELETE'
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Tag deleted successfully!' });
        loadTags();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to delete tag.' });
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
      setMessage({ type: 'error', text: 'Failed to delete tag.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="sticky top-0 z-10">
        <ModernSettingHeader
          title="CRM Tags"
          description="Manage tags that can be applied to Shopify orders"
          icon={<Tags className="w-6 h-6" />}
          buttonText={!isAddingNew ? "Add Tag" : undefined}
          onClick={!isAddingNew ? () => setIsAddingNew(true) : undefined}
        />
      </div>
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          {/* Message Alert */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          {/* Add New Tag Form */}
          {isAddingNew && (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Add New Tag</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tag Name *
                  </label>
                  <input
                    type="text"
                    value={newTag.tag_name}
                    onChange={(e) => setNewTag({ ...newTag, tag_name: e.target.value })}
                    placeholder="e.g., VIP Customer, Repeat Buyer"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newTag.tag_description}
                    onChange={(e) => setNewTag({ ...newTag, tag_description: e.target.value })}
                    placeholder="Brief description of this tag"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setIsAddingNew(false);
                      setNewTag({ tag_name: '', tag_description: '' });
                    }}
                    className="flex items-center space-x-1 px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
                  >
                    <X className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleAddTag}
                    disabled={isSaving || !newTag.tag_name.trim()}
                    className="flex items-center space-x-1 px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Save className="h-4 w-4" />
                    <span>{isSaving ? 'Saving...' : 'Save Tag'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tags List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Tags ({tags.length})
              </h3>
            </div>

            {tags.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Tags className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No CRM tags configured yet.</p>
                <p className="text-sm mt-1">Click "Add New Tag" to create your first tag.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {tags.map((tag) => (
                  <div key={tag.uuid} className="px-6 py-4">
                    {editingTag?.uuid === tag.uuid ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Tag Name
                          </label>
                          <input
                            type="text"
                            value={editingTag.tag_name}
                            onChange={(e) =>
                              setEditingTag({ ...editingTag, tag_name: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={editingTag.tag_description || ''}
                            onChange={(e) =>
                              setEditingTag({ ...editingTag, tag_description: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`active-${tag.uuid}`}
                            checked={editingTag.is_active}
                            onChange={(e) =>
                              setEditingTag({ ...editingTag, is_active: e.target.checked })
                            }
                            className="rounded border-gray-300 text-brandColor focus:ring-brandColor"
                          />
                          <label
                            htmlFor={`active-${tag.uuid}`}
                            className="text-sm text-gray-700 dark:text-gray-300"
                          >
                            Active
                          </label>
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setEditingTag(null)}
                            className="flex items-center space-x-1 px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all text-sm"
                          >
                            <X className="h-3.5 w-3.5" />
                            <span>Cancel</span>
                          </button>
                          <button
                            onClick={handleUpdateTag}
                            disabled={isSaving}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all text-sm"
                          >
                            <Save className="h-3.5 w-3.5" />
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              tag.is_active ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          />
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                              {tag.tag_name}
                            </h4>
                            {tag.tag_description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {tag.tag_description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setEditingTag(tag)}
                            className="p-2 text-gray-500 hover:text-brandColor hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
                            title="Edit tag"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTag(tag)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                            title="Delete tag"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-6 mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
              About CRM Tags
            </h3>
            <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
              <li>CRM tags are automatically added to Shopify orders created from the CRM</li>
              <li>Use tags to track order sources, customer types, or special promotions</li>
              <li>Tags appear in your Shopify admin and can be used for filtering orders</li>
              <li>Inactive tags will not be applied to new orders</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmTagsPage;
